import type React from 'react';
import { useEffect, useState } from 'react';
import { Button } from 'tradera/components/atoms/button/button';
import { SideMenu } from 'tradera/components/side-menu/side-menu';
import { ScrollableModalContent } from 'tradera/components/scrollable-modal/scrollable-modal-content';
import { RadioSwitch } from 'tradera/components/form-controls/radio-switch/radio-switch';
import { WishListItems } from './wish-list-items';
import { useTranslator } from 'tradera/lang/translate';
import { closeWishListSideSheet } from 'tradera/state/ui/reducer';
import { HeaderWithCloseButton } from 'tradera/components/scrollable-modal/header-with-close-button';
import { SearchInput } from 'tradera/components/form-controls/search-input/search-input';
import { useAppDispatch, useAppSelector } from 'tradera/state/hooks';
import { WishListShare } from '../wish-list-share/wish-list-share';
import { useIsFeatureEnabled } from 'tradera/hooks/use-is-feature-enabled';
import { WishListShareButton } from '../wish-list-share/wish-list-share-button';
import { useOnNavigation } from 'tradera/hooks/use-on-navigation';

export const WishListSideSheet = () => {
    const { t } = useTranslator();

    const dispatch = useAppDispatch();
    const { isOpen } = useAppSelector((state) => state.ui.wishListSideSheet);

    const wishlistShareEnabled = useIsFeatureEnabled('wish-list-share');

    const [isShareOpen, setIsShareOpen] = useState(false);
    const [wishlistEmpty, setWishlistEmpty] = useState(false);
    const [showShareToolTip, setShowShareToolTip] = useState(false);

    const [showMayBeMoreResultsText, setShowMayBeMoreResultsText] =
        useState(false);

    const [searchTerm, setSearchTerm] = useState('');

    const filterOptions = [
        { value: 'all', name: t('wishlist_sidesheet_filter_all') },
        {
            value: 'latestSaved',
            name: t('wishlist_latest_saved'),
            // TODO: This "New!" badge can be removed after june 2025
            badgeLabel: t('myt_wishlist_new_tab_badge'),
        },
        {
            value: 'ended',
            name: t('wishlist_sidesheet_filter_ended'),
        },
    ];

    const [selectedFilter, setSelectedFilter] = useState('all');

    const handleFilterChange = (filter: string) => setSelectedFilter(filter);

    const handleSearch = ({ target }: React.ChangeEvent<HTMLInputElement>) =>
        setSearchTerm(target.value);

    const handleSearchClear = () => setSearchTerm('');

    const handleDismiss = () => dispatch(closeWishListSideSheet());

    useOnNavigation(handleDismiss);

    useEffect(() => {
        if (wishlistShareEnabled && isOpen) {
            setTimeout(() => {
                setShowShareToolTip(true);
            }, 350);
        }
    }, [wishlistShareEnabled, isOpen]);

    useEffect(() => {
        if (isOpen) return;

        handleSearchClear();
        setSelectedFilter('all');
    }, [isOpen]);

    return (
        <>
            <SideMenu show={isOpen} onDismiss={handleDismiss} width="30rem">
                <ScrollableModalContent
                    onClose={handleDismiss}
                    header={
                        <>
                            <HeaderWithCloseButton
                                menu={
                                    wishlistEmpty ||
                                    !wishlistShareEnabled ? undefined : (
                                        <WishListShareButton
                                            showTooltip={showShareToolTip}
                                            onClick={() => {
                                                handleDismiss();
                                                setIsShareOpen(true);
                                            }}
                                            gaEventLabel="Sidesheet"
                                        />
                                    )
                                }
                                onClose={handleDismiss}
                                title={t('wishlist_sidesheet_header')}
                                border
                            />
                            <div className="px-2 px-sm-3 mb-3">
                                <RadioSwitch
                                    id="wish-list-filter"
                                    options={filterOptions}
                                    defaultChecked={selectedFilter}
                                    handleOnChange={handleFilterChange}
                                    className="my-2"
                                />
                                <SearchInput
                                    placeholder={t(
                                        'wishlist_sidesheet_search_placeholder',
                                    )}
                                    allowClear
                                    value={searchTerm}
                                    maxLength={98}
                                    onChange={handleSearch}
                                />
                            </div>
                        </>
                    }
                    className="bg-white"
                    innerClassName="flex-grow-1"
                    footer={
                        <>
                            {showMayBeMoreResultsText && (
                                <i className="text-muted d-block mb-1 text-wrap">
                                    {t(
                                        'wishlist_sidesheet_may_be_more_resulsts',
                                    )}
                                </i>
                            )}
                            <Button
                                href="/my/wish-list"
                                variant="outline"
                                fluid
                                onClick={handleDismiss}>
                                {t('wishlist_sidesheet_to_overview')}
                            </Button>
                        </>
                    }>
                    <WishListItems
                        filter={selectedFilter}
                        searchTerm={searchTerm}
                        setShowMayBeMoreResultsText={
                            setShowMayBeMoreResultsText
                        }
                        setWishlistEmpty={setWishlistEmpty}
                    />
                </ScrollableModalContent>
            </SideMenu>
            {isShareOpen && wishlistShareEnabled && (
                <WishListShare onClose={() => setIsShareOpen(false)} />
            )}
        </>
    );
};
